import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Download,
  Filter,
  RefreshCw,
  Timer,
  TrendingUp,
  Users,
} from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import SourcingRfpService from '../../services/sourcingRFPService';

const dayDiff = (a, b) => {
  if (!a || !b) return null;
  const start = new Date(a);
  const end = new Date(b);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)));
};

const SourcingReportsDashboard = () => {
  const { success, error: showError, toasts, removeToast } = useToast();

  const [rfps, setRfps] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    rfpStatus: 'ALL',
    windowDays: '90',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [rfpRes, responsesRes] = await Promise.all([
        SourcingRfpService.getRfps(),
        SourcingRfpService.getResponses(''),
      ]);

      const allRfps = rfpRes.data?.items || rfpRes.items || rfpRes.data || [];
      setRfps(Array.isArray(allRfps) ? allRfps : []);
      setResponses(Array.isArray(responsesRes.data) ? responsesRes.data : []);
    } catch (err) {
      showError('Failed to load sourcing reports data.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredRfps = useMemo(() => {
    const now = new Date();
    const days = Number(filters.windowDays || 90);

    return rfps.filter((rfp) => {
      const matchesStatus =
        filters.rfpStatus === 'ALL' ||
        String(rfp.status || '').toUpperCase() === filters.rfpStatus;

      const created = new Date(rfp.createdAt || rfp.updatedAt || Date.now());
      const ageDays = Math.round((now - created) / (1000 * 60 * 60 * 24));
      const matchesWindow = Number.isNaN(ageDays) ? true : ageDays <= days;

      return matchesStatus && matchesWindow;
    });
  }, [filters.rfpStatus, filters.windowDays, rfps]);

  const filteredRfpIds = useMemo(
    () => new Set(filteredRfps.map((r) => String(r._id))),
    [filteredRfps],
  );

  const filteredResponses = useMemo(
    () =>
      responses.filter((res) => {
        const rfpId = String(res.rfpId?._id || res.rfpId || '');
        return filteredRfpIds.has(rfpId);
      }),
    [filteredRfpIds, responses],
  );

  const rfpStatusSummary = useMemo(() => {
    const bucket = {};
    filteredRfps.forEach((rfp) => {
      const status = String(rfp.status || 'UNKNOWN').toUpperCase();
      bucket[status] = (bucket[status] || 0) + 1;
    });
    return Object.entries(bucket).sort((a, b) => b[1] - a[1]);
  }, [filteredRfps]);

  const vendorPerformance = useMemo(() => {
    const map = new Map();

    filteredResponses.forEach((res) => {
      const vendorName = res.vendorId?.name || 'Unknown Vendor';
      const row = map.get(vendorName) || {
        vendorName,
        bids: 0,
        awarded: 0,
        scoreTotal: 0,
        scoredCount: 0,
      };

      row.bids += 1;
      if (String(res.status || '').toUpperCase() === 'AWARDED')
        row.awarded += 1;

      const weighted = Number(res.evaluationId?.weightedScore);
      if (Number.isFinite(weighted)) {
        row.scoreTotal += weighted;
        row.scoredCount += 1;
      }

      map.set(vendorName, row);
    });

    return Array.from(map.values())
      .map((row) => ({
        ...row,
        avgWeightedScore: row.scoredCount
          ? row.scoreTotal / row.scoredCount
          : 0,
        winRate: row.bids ? (row.awarded / row.bids) * 100 : 0,
      }))
      .sort((a, b) => b.avgWeightedScore - a.avgWeightedScore)
      .slice(0, 5);
  }, [filteredResponses]);

  const cycleTime = useMemo(() => {
    const closed = filteredRfps.filter((rfp) =>
      ['AWARDED', 'CONTRACT', 'CONTRACTED', 'CLOSED'].includes(
        String(rfp.status || '').toUpperCase(),
      ),
    );

    const durations = closed
      .map((rfp) => dayDiff(rfp.createdAt, rfp.updatedAt))
      .filter((d) => Number.isFinite(d));

    const averageDays = durations.length
      ? durations.reduce((sum, d) => sum + d, 0) / durations.length
      : 0;

    return {
      averageDays,
      measuredCount: durations.length,
    };
  }, [filteredRfps]);

  const approvalDelays = useMemo(() => {
    const pendingApproval = filteredRfps.filter((rfp) =>
      ['PENDING_APPROVAL', 'APPROVAL', 'REVIEW'].includes(
        String(rfp.status || '').toUpperCase(),
      ),
    );

    const completedApproval = filteredRfps.filter((rfp) =>
      ['AWARDED', 'APPROVED'].includes(String(rfp.status || '').toUpperCase()),
    );

    const delayDurations = completedApproval
      .map((rfp) => dayDiff(rfp.createdAt, rfp.updatedAt))
      .filter((d) => Number.isFinite(d));

    const averageDelay = delayDurations.length
      ? delayDurations.reduce((sum, d) => sum + d, 0) / delayDurations.length
      : 0;

    return {
      pendingCount: pendingApproval.length,
      averageDelay,
    };
  }, [filteredRfps]);

  const exportReport = () => {
    const lines = [
      'Metric,Value',
      `RFP Window (days),${filters.windowDays}`,
      `RFP Status Filter,${filters.rfpStatus}`,
      `Total Filtered RFPs,${filteredRfps.length}`,
      `Cycle Time Avg (days),${cycleTime.averageDays.toFixed(1)}`,
      `Approval Delay Avg (days),${approvalDelays.averageDelay.toFixed(1)}`,
      '',
      'RFP Status Summary,Count',
      ...rfpStatusSummary.map(([status, count]) => `${status},${count}`),
      '',
      'Vendor Performance (Top),Bids,Awarded,Avg Weighted Score,Win Rate %',
      ...vendorPerformance.map(
        (v) =>
          `${v.vendorName},${v.bids},${v.awarded},${v.avgWeightedScore.toFixed(2)},${v.winRate.toFixed(2)}`,
      ),
    ];

    const blob = new Blob([lines.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sourcing-reports-dashboard.csv';
    a.click();
    URL.revokeObjectURL(url);
    success('Report export completed.');
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Reports Dashboard"
        description="Track sourcing performance with status, vendor, cycle time, and approval delay insights"
        actions={[
          {
            icon: (
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            ),
            variant: 'outline',
            onClick: loadData,
          },
          {
            label: 'Export',
            icon: <Download size={16} />,
            variant: 'primary',
            onClick: exportReport,
          },
        ]}
      />

      <div className="page-container">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={16} className="text-blue-600" />
            <h3 className="text-base font-semibold text-gray-800">Filter</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                RFP Status
              </label>
              <select
                value={filters.rfpStatus}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, rfpStatus: e.target.value }))
                }
                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm"
              >
                <option value="ALL">All</option>
                <option value="DRAFT">Draft</option>
                <option value="PUBLISHED">Published</option>
                <option value="EVALUATION">Evaluation</option>
                <option value="AWARDED">Awarded</option>
                <option value="APPROVED">Approved</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                Window
              </label>
              <select
                value={filters.windowDays}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    windowDays: e.target.value,
                  }))
                }
                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm"
              >
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="180">Last 180 days</option>
                <option value="365">Last 365 days</option>
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h4 className="text-sm font-semibold text-gray-800 mb-3">
              RFP Status Summary
            </h4>
            <div className="space-y-2">
              {rfpStatusSummary.slice(0, 4).map(([status, count]) => (
                <div
                  key={status}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-gray-600">{status}</span>
                  <span className="font-bold text-gray-900">{count}</span>
                </div>
              ))}
              {rfpStatusSummary.length === 0 && (
                <p className="text-sm text-gray-500">
                  No data for selected filters.
                </p>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h4 className="text-sm font-semibold text-gray-800 mb-3">
              Vendor Performance
            </h4>
            <div className="space-y-2">
              {vendorPerformance.slice(0, 3).map((vendor) => (
                <div key={vendor.vendorName} className="text-sm">
                  <p className="font-semibold text-gray-900">
                    {vendor.vendorName}
                  </p>
                  <p className="text-gray-500">
                    Score {vendor.avgWeightedScore.toFixed(1)} | Win{' '}
                    {vendor.winRate.toFixed(1)}%
                  </p>
                </div>
              ))}
              {vendorPerformance.length === 0 && (
                <p className="text-sm text-gray-500">No scored vendors yet.</p>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-800">
                Cycle Time
              </h4>
              <Timer size={16} className="text-amber-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {cycleTime.averageDays.toFixed(1)} days
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Based on {cycleTime.measuredCount} completed events.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-800">
                Approval Delays
              </h4>
              <TrendingUp size={16} className="text-rose-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {approvalDelays.averageDelay.toFixed(1)} days
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {approvalDelays.pendingCount} RFPs currently waiting for approval.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <Users size={16} className="text-indigo-600" />
            <h4 className="text-sm font-semibold text-gray-800">
              Vendor Performance Details
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-3">Vendor</th>
                  <th className="py-2 px-3">Bids</th>
                  <th className="py-2 px-3">Awarded</th>
                  <th className="py-2 px-3">Avg Weighted Score</th>
                  <th className="py-2 pl-3">Win Rate</th>
                </tr>
              </thead>
              <tbody>
                {vendorPerformance.map((row) => (
                  <tr key={row.vendorName} className="border-b">
                    <td className="py-2 pr-3 font-semibold text-gray-900">
                      {row.vendorName}
                    </td>
                    <td className="py-2 px-3">{row.bids}</td>
                    <td className="py-2 px-3">{row.awarded}</td>
                    <td className="py-2 px-3">
                      {row.avgWeightedScore.toFixed(2)}
                    </td>
                    <td className="py-2 pl-3">{row.winRate.toFixed(2)}%</td>
                  </tr>
                ))}
                {vendorPerformance.length === 0 && (
                  <tr>
                    <td className="py-3 text-gray-500" colSpan={5}>
                      No vendor performance records found for current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default SourcingReportsDashboard;
